import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, Skeleton, VStack, Modal, HStack } from 'native-base';
import { StoreLocations } from './StoreLocations';
import {
    FulfillmentOptionsCardProp,
    MenuType,
    Retailer,
    useCreateCheckoutMutation,
    useDutchieStore,
    useRetailersQuery,
    defaultCategories,
    OrderType,
    Hours,
    Maybe,
    useI18n,
    useSpringBigStore
} from '@springbig/shared';
import { SBDropdown } from '~/shared/SBDropdown';
import { Caption, H3 } from '~/shared/Typography';
import { useGeolocated } from 'react-geolocated';
import { FullfillmentOptions } from './FullfillmentOptions';
import { useShopStyles } from '~/shared/styles';
import { isBefore, isAfter, format, parse } from 'date-fns';

export type StoreModalProp = {
    showStore: boolean;
    setShowStore: () => void;
};

export const storeSchedule = (orderType: OrderType, retailer: Retailer): boolean => {

    let fullfillmentOptionHours : Maybe<Hours> | undefined;
    if (orderType) {
        switch (orderType) {
            case OrderType.CurbsidePickup:
                fullfillmentOptionHours = retailer?.hours.curbsidePickup;
                break;
            case OrderType.Delivery:
                fullfillmentOptionHours = retailer?.hours.delivery;
                break;
            case OrderType.DriveThruPickup:
                fullfillmentOptionHours = retailer?.hours.driveThruPickup;
                break;
            case OrderType.Pickup:
                fullfillmentOptionHours = retailer?.hours.pickup;
                break;
        }
    }

    if (fullfillmentOptionHours) {

        const currentDayOfTheWeek = format(new Date(), 'EEEE');
        const todayHours = fullfillmentOptionHours[currentDayOfTheWeek as keyof typeof fullfillmentOptionHours];
        if (todayHours) {
            const startHour = parse(todayHours.start, 'h:mm a', new Date());
            const endHour = parse(todayHours.end, 'h:mm a', new Date());

            return todayHours.active && isAfter(new Date(), startHour) && isBefore(new Date(), endHour);
        }
    }

    return true;
};

export const StoreModal = ({ showStore, setShowStore }: StoreModalProp) => {

    const {
        retailer,
        setRetailer,
        fullfillmentOption,
        menuType,
        setMenuType,
        setMenuCategories,
        setFullfillmentOption,
        setCheckout,
        setShowStoreBottomSheet
    } = useDutchieStore();

    const { mutate: getRetailers, data: retailersData, isLoading } = useRetailersQuery();
    const [ isMerchantGeoLocation, setIsMerchantGeoLocation ] = useState(false);
    const { section_background, pressable_text, button_background, button_text } = useShopStyles();

    const [internalRetailer, setInternalRetailer] = useState<Retailer | undefined>();
    const [fullfillmentOptions, setFullfillmentOptions] = useState<Array<FulfillmentOptionsCardProp>>([]);
    const [internalMenuType, setInternalMenuType] = useState<MenuType | undefined>();
    const [internalFullfillmentOption, setInternalFullfillmentOption] = useState<FulfillmentOptionsCardProp | undefined>();
    const [showComfirmationModal, setShowConfirmationModal] = useState(false);
    const [selectedDistance, setSelectedDistance] = useState<number>(30);
    const distances = [30, 50, 100];
    const { mutate: createCheckout } = useCreateCheckoutMutation();
    const { t } = useI18n();
    const { merchant } = useSpringBigStore();

    const { coords, isGeolocationEnabled } =
        useGeolocated({
            positionOptions: {
                enableHighAccuracy: true,
            },
        });

    useEffect(() => {

        if (isGeolocationEnabled && coords && merchant) {
            const variables = {
                'latitude': process.env.NODE_ENV === 'development' ? 43.0459 : coords.latitude,
                'longitude': process.env.NODE_ENV === 'development' ? -81.25999999999 : coords.longitude,
                'maxDistance': selectedDistance,
                'unit': 'MI',
            };
            getRetailers(variables, { onSettled: (data) => {
                if (data?.retailersNearLocation?.length === 0 && merchant.latitude && merchant.longitude) {
                    const variables = {
                        'latitude': parseFloat(merchant.latitude),
                        'longitude': parseFloat(merchant.longitude),
                        'maxDistance': selectedDistance,
                        'unit': 'MI',
                    };
                    setIsMerchantGeoLocation(true);
                    getRetailers(variables);
                }
            } });
        }
    }, [isGeolocationEnabled, coords, getRetailers, selectedDistance, merchant]);


    useEffect(() => {

        const availableFulfillmentOptions: Array<FulfillmentOptionsCardProp> = [];
        if (retailer?.fulfillmentOptions.pickup) {
            availableFulfillmentOptions.push({ key: OrderType.Pickup, label: 'pickup', isOpen: storeSchedule(OrderType.Pickup, retailer) });
        }

        if (retailer?.fulfillmentOptions.curbsidePickup) {
            availableFulfillmentOptions.push({ key: OrderType.CurbsidePickup, label: 'curbside pickup', isOpen: storeSchedule(OrderType.CurbsidePickup, retailer) });
        }

        if (retailer?.fulfillmentOptions.driveThruPickup) {
            availableFulfillmentOptions.push({ key: OrderType.DriveThruPickup, label: 'drive thru pickup', isOpen: storeSchedule(OrderType.DriveThruPickup, retailer) });
        }

        if (retailer?.fulfillmentOptions.delivery) {
            availableFulfillmentOptions.push({ key: OrderType.Delivery, label: 'delivery', isOpen: storeSchedule(OrderType.Delivery, retailer) });
        }

        if (fullfillmentOption) {
            setInternalFullfillmentOption(fullfillmentOption);
        }
        else if (availableFulfillmentOptions && availableFulfillmentOptions.find((e) => e.isOpen)) {
            setInternalFullfillmentOption(availableFulfillmentOptions.find((e) => e.isOpen));
        }

        if (retailer) {
            setInternalRetailer(retailer);
        }

        if (menuType) {
            setInternalMenuType(menuType);
        }

        setFullfillmentOptions(availableFulfillmentOptions);
    }, [retailer]);

    const applyOptions = () => {

        if (internalRetailer === retailer) {

            if (internalMenuType) {
                setMenuType(internalMenuType);
            }

            if (internalFullfillmentOption) {
                setFullfillmentOption(internalFullfillmentOption);
            }

            setShowStore();
        }
        else {
            if (showComfirmationModal) {
                if (internalRetailer) {
                    setRetailer(internalRetailer);
                    createCheckout({
                        pricingType: internalMenuType,
                        orderType: internalFullfillmentOption?.key,
                        retailerId: internalRetailer?.id,
                    }, {
                        onSuccess(data) {

                            if (data) {
                                setCheckout(data.createCheckout);
                                setShowStore();
                                setMenuCategories(defaultCategories);
                            }
                        },
                        onError(error) {

                            console.log(error.data.errors[0].message);
                        }
                    });
                }

                if (internalMenuType) {
                    setMenuType(internalMenuType);
                }

                if (internalFullfillmentOption) {
                    setFullfillmentOption(internalFullfillmentOption);
                }
            }
            else {
                setShowConfirmationModal(true);
            }
        }
    };

    return (
        <Modal isOpen={showStore}>
            <Modal.Content>
                <Modal.Header>{ showComfirmationModal ? t('shop.alert') : t('shop.location_and_method') }</Modal.Header>
                <Modal.CloseButton
                    bg={section_background}
                    _icon={{
                        color: pressable_text,
                    }}
                    onPress={setShowStore}
                />
                <Modal.Body>
                    { showComfirmationModal ? (
                        <VStack alignItems={'center'} space={4}>
                            <Caption textAlign={'center'}>
                                { t('shop.due_to_differences') }
                            </Caption>
                            <Caption textAlign={'center'}>
                                { t('shop.would_you_like_to_continue') }
                            </Caption>
                        </VStack>
                    ) : (
                        <ScrollView>
                            <SBDropdown value={internalFullfillmentOption?.label}>
                                <FullfillmentOptions
                                    fullfillmentOptions={fullfillmentOptions}
                                    setFullfillmentOption={setInternalFullfillmentOption}
                                />
                            </SBDropdown>
                            <Caption alignSelf={'center'} py={2}>
                                { t('shop.at') }
                            </Caption>
                            { isLoading || !isGeolocationEnabled || !coords ?
                                <Skeleton />
                                :
                                <SBDropdown value={internalRetailer?.name ?? retailer?.name}>
                                    <VStack py={4}>
                                        <VStack alignItems={'center'} space={4}>
                                            <H3>{ t('shop.distance') }</H3>
                                            <HStack space={4}>
                                                { distances.map((distance, index) =>

                                                    <Pressable
                                                        key={index}
                                                        onPress={() => setSelectedDistance(distance)}
                                                        bg={selectedDistance === distance ? button_background : section_background}
                                                        rounded={'sm'}
                                                        px={2}
                                                    >
                                                        <Caption
                                                            color={selectedDistance === distance ? button_text : pressable_text}
                                                        >
                                                            { distance } { retailersData?.retailersNearLocation[0]?.locationDetail?.unit === 'MI' ? 'miles' : 'km' }
                                                        </Caption>
                                                    </Pressable>
                                                )}
                                            </HStack>
                                        </VStack>
                                        {retailersData && retailersData.retailersNearLocation.length !== 0 ? <StoreLocations
                                            setMenuType={setInternalMenuType}
                                            setRetailer={setInternalRetailer}
                                            type="modal"
                                            stores={isMerchantGeoLocation ? 
                                                retailersData?.retailersNearLocation?.sort((a,b) => a?.retailer?.name.localeCompare(b?.retailer?.name)) :
                                                retailersData?.retailersNearLocation
                                            }
                                            setFullfillmentOption={setInternalFullfillmentOption}
                                            t={t}
                                            internalRetailer={internalRetailer}
                                            isMerchantGeoLocation={isMerchantGeoLocation}
                                        /> : <Caption textAlign={'center'} py={2}>Shopping is Not Avaliable in your current location</Caption>}
                                    </VStack>
                                </SBDropdown>
                            }
                            { fullfillmentOptions.find((e) => !e.isOpen) ?
                                <>
                                    <Caption p={2} color={'gray'} fontSize={12}>{ t('shop.some_fulfillment_options') }
                                        <Pressable onPress={() => {

                                            setShowStoreBottomSheet(true);
                                            setShowStore();
                                        }}>
                                            <Caption color={pressable_text} fontSize={12}> { t('shop.see_fulfillment_hours') }</Caption>
                                        </Pressable>
                                    </Caption>
                                </>
                                :
                                <></>
                            }
                        </ScrollView>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {showComfirmationModal ? <Pressable px={4} onPress={() => {

                        setShowConfirmationModal(false);
                    }}>
                        <Caption>{t('general.back').toLowerCase()}</Caption>
                    </Pressable> : <></>}
                    <Pressable
                        onPress={() => applyOptions()}
                    >
                        <Caption>{ t('shop.apply') }</Caption>
                    </Pressable>
                </Modal.Footer>
            </Modal.Content>
        </Modal>
    );
};
